import React from 'react';

import { ReactComponent as ArrowSVG } from '@pkg/assets/floorGame/arrow.svg';
import { CurrencyIcon } from '@pkg/components';
import {
    Content,
    ContentRow,
    Label,
    PercentLabel,
    PriceGroup,
    PriceGroupLoader,
    Value,
    ValuePrefix,
    ValueText,
} from '@pkg/components/Common/CardTile/CardBottom';
import { convertWeiToEth } from '@pkg/components/Sections/Groups/helpers';
import { getCurrencyFromChain } from '@pkg/hooks';

import { CollectionChainType, type SupportedTokens } from '@app/client';
import { type FloorAskType } from '@app/pages/NFTDetailView/components/MainStage/components/NFTInfo/NFTInfo.data';
import { CURRENCY_DECIMALS, useBaseCurrencyStore, useExchangeRatesStore } from '@app/store/zustand';

interface CardPricesProps {
    listPriceEth?: string | null;
    listPriceData?: FloorAskType['price'];
    last_buy?: number | null;
    last_sell?: number | null;
    floor_price_change?: number | null;
    useTokenPricing?: boolean;
    chain?: CollectionChainType;
}

export const CardFantasyPrices = ({
    listPriceEth,
    listPriceData,
    floor_price_change,
    useTokenPricing = false,
    chain = CollectionChainType.Ethereum,
}: CardPricesProps) => {
    const baseCurrency = useBaseCurrencyStore((state) => state.currency);
    const chainCurrency = getCurrencyFromChain(chain?.toLowerCase() as SupportedTokens);
    const formatCurrency = useBaseCurrencyStore((state) => state.formatCurrency);
    const convertCurrency = useExchangeRatesStore((state) => state.convertCurrency);

    const floorPriceEth = convertWeiToEth(parseInt(listPriceEth || '0'), CURRENCY_DECIMALS.eth);
    const converted = convertCurrency(chainCurrency, baseCurrency, floorPriceEth);
    const valueFormatted = formatCurrency(baseCurrency, converted, true);
    const nativePrice = listPriceData?.amount?.native;
    const rawPriceConverted =
        Number(listPriceData?.amount?.raw) / Math.pow(10, Number(listPriceData?.currency?.decimals));

    const currency = useTokenPricing ? chainCurrency : baseCurrency;
    const value = useTokenPricing ? nativePrice || rawPriceConverted : valueFormatted;

    const prefix =
        currency === 'usd' ? null : (
            <ValuePrefix>
                <CurrencyIcon currency={currency} theme="dark" />
            </ValuePrefix>
        );

    const loading = Boolean(listPriceEth && floorPriceEth <= 0);

    return (
        <PriceGroup>
            <PriceGroupLoader isLoading={loading} $shorter>
                <Label>Price</Label>
            </PriceGroupLoader>

            <PriceGroupLoader isLoading={loading}>
                <ContentRow>
                    <Content>
                        <Value>
                            {prefix}
                            <ValueText>{value}</ValueText>
                        </Value>

                        {floor_price_change ? (
                            <PercentLabel $isDesc={floor_price_change < 0}>
                                <ArrowSVG />
                                <span>{Math.abs(floor_price_change).toFixed(1)}%</span>
                            </PercentLabel>
                        ) : null}
                    </Content>
                </ContentRow>
            </PriceGroupLoader>
        </PriceGroup>
    );
};
